/**
 * PlanDetailPage - Per-Plan Access-Aware Plan View
 *
 * Routes users to appropriate views based on their access level for THIS plan:
 * - ADMIN/Owner: Full plan view with all steps, facts, and management features
 * - ROLE_MEMBER: Filtered view showing only assigned steps (via projectedPlan)
 * - No access: Shows access denied
 *
 * AUTHORIZATION NOTE: Access is per-(planId, personId) scoped.
 * A person who is ADMIN in Plan A may be ROLE_MEMBER in Plan B.
 * The source of truth is plans/{planId}/participants/{personId}.accessLevel
 */

import React, { useCallback, useMemo, useEffect, useRef, useState } from 'react';
import {
  Container,
  Box,
  Button,
  CircularProgress,
  Alert,
  Typography,
  LinearProgress,
} from '@mui/material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LockIcon from '@mui/icons-material/Lock';
import { doc, onSnapshot } from 'firebase/firestore';
import { GET_PLAN, UPDATE_STEP_CONTEXT, TOGGLE_STEP_COMPLETION, GET_PROJECTED_PLAN } from '../graphql/queries';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { PlanViewerProvider, ParticipantAccessLevel } from '../contexts/PlanViewerContext';
import { colors } from '../theme/colors';
import { getPlanProgress } from '../utils/taskTree';
import { PlanTabContainer, TabValue } from '../components/plan-tabs';
import PortalShell, { type PortalTab } from '../components/PortalShell';
import PlanHeaderBar from '../components/PlanHeaderBar';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useToast } from '../contexts/ToastContext';
import { StepStatus } from '../types/generated/graphql';

function PlanDetailPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id, tab, stepId } = useParams<{ id: string; tab?: string; stepId?: string }>();
  const { user, loading: authLoading } = useAuth();
  const { showToast } = useToast();
  const previousStepStatusesRef = useRef<Record<string, string>>({});
  const [genJobMessage, setGenJobMessage] = useState<string | null>(null);

  // Redirect to auth if not logged in
  useEffect(() => {
    if (!authLoading && !user) {
      // Store current path for redirect after login
      sessionStorage.setItem('redirectAfterLogin', location.pathname);
      navigate('/auth');
    }
  }, [user, authLoading, navigate, location.pathname]);

  // New portal tabs: run | assets | people
  const PORTAL_TABS = new Set<string>(['run', 'assets', 'people']);
  const isPortalTab = tab ? PORTAL_TABS.has(tab) : false;
  const portalTab: PortalTab = (isPortalTab ? tab : 'run') as PortalTab;

  // Derive current tab from URL params (legacy tabs)
  const currentTab: TabValue = useMemo(() => {
    if (tab === 'tasks' || tab === 'when' || tab === 'outputs') {
      return tab;
    }
    // If stepId is present, we're on tasks tab
    if (stepId) {
      return 'tasks';
    }
    return 'overview';
  }, [tab, stepId]);

  // Handle tab change - navigate to new URL
  const handleTabChange = useCallback(
    (newTab: TabValue) => {
      if (newTab === 'overview') {
        navigate(`/app/plans/${id}`);
      } else {
        navigate(`/app/plans/${id}/${newTab}`);
      }
    },
    [id, navigate]
  );

  // Handle portal tab change (new Run/Assets/People)
  const handlePortalTabChange = useCallback(
    (newTab: PortalTab) => {
      navigate(`/plans/${id}/${newTab}`);
    },
    [id, navigate]
  );

  // Handle step selection - navigate to step URL
  const handleStepSelect = useCallback(
    (newStepId: string | null) => {
      if (newStepId) {
        navigate(`/app/plans/${id}/tasks/${newStepId}`);
      } else {
        navigate(`/app/plans/${id}/tasks`);
      }
    },
    [id, navigate]
  );

  // FIRST: Query projected plan to determine access level
  // This is the source of truth for what the user can see
  const {
    loading: projectedLoading,
    error: projectedError,
    data: projectedData,
  } = useQuery(GET_PROJECTED_PLAN, {
    variables: { planId: id || '' },
    skip: !id || !user,
  });

  // Extract access info from projected plan
  const accessLevel = projectedData?.projectedPlan?.viewerAccessLevel as ParticipantAccessLevel | undefined;
  const isOwner = projectedData?.projectedPlan?.isOwner ?? false;
  const viewerPersonId = projectedData?.projectedPlan?.viewerPersonId || null;
  const roleIds = projectedData?.projectedPlan?.viewerRoleIds || [];
  useDocumentTitle('Plans', projectedData?.projectedPlan?.goal);

  // Determine if user has ADMIN access (owner or explicit ADMIN)
  const hasAdminAccess = isOwner || accessLevel === 'ADMIN';

  // Redirect ROLE_MEMBER users to the dedicated participant workspace
  useEffect(() => {
    if (!projectedLoading && projectedData?.projectedPlan) {
      const { viewerAccessLevel, isOwner: ownerFlag } = projectedData.projectedPlan;
      if (!ownerFlag && viewerAccessLevel === 'ROLE_MEMBER') {
        navigate(`/app/plans/${id}/participant`, { replace: true });
      }
    }
  }, [projectedLoading, projectedData, id, navigate]);

  // SECOND: Only fetch full plan if user has ADMIN access
  // ROLE_MEMBER users should NEVER read the full plan doc
  const {
    loading: planLoading,
    error: planError,
    data: planData,
  } = useQuery(GET_PLAN, {
    variables: { id: id || '' },
    skip: !id || !user || !hasAdminAccess || !projectedData,
    // Poll when viewing plan so we detect async step completion (e.g. FOIA connector)
    pollInterval: id && hasAdminAccess ? 15_000 : 0,
  });

  const [updateStepContext] = useMutation(UPDATE_STEP_CONTEXT, {
    refetchQueries: [
      { query: GET_PROJECTED_PLAN, variables: { planId: id || '' } },
      ...(hasAdminAccess ? [{ query: GET_PLAN, variables: { id: id || '' } }] : []),
    ],
  });

  const [toggleStepCompletion] = useMutation(TOGGLE_STEP_COMPLETION, {
    refetchQueries: [
      { query: GET_PROJECTED_PLAN, variables: { planId: id || '' } },
      ...(hasAdminAccess ? [{ query: GET_PLAN, variables: { id: id || '' } }] : []),
    ],
    awaitRefetchQueries: false,
  });

  // Handler for toggling step completion
  const handleToggleStepCompletion = (
    targetStepId: string,
    completed: boolean
  ) => {
    if (!id) return;

    toggleStepCompletion({
      variables: {
        planId: id,
        stepId: targetStepId,
        completed,
      },
    });
  };

  // Handler for updating step context
  const handleUpdateStepContext = async (
    targetStepId: string,
    context: { notes?: string | null; links?: string[] }
  ) => {
    if (!id) return;

    await updateStepContext({
      variables: {
        planId: id,
        stepId: targetStepId,
        context: {
          notes: context.notes,
          links: context.links || [],
        },
      },
    });
  };

  // When a step transitions to Completed (e.g. after async connector poll), show toast with deep link.
  // Must run before any early return so hooks are called in the same order every render.
  useEffect(() => {
    if (!id) return;
    let steps: { id: string; status: string; title: string }[] | undefined;
    let planGoal: string | undefined;
    if (hasAdminAccess && planData?.planapusPlan) {
      steps = planData.planapusPlan.steps;
      planGoal = planData.planapusPlan.goal;
    } else if (projectedData?.projectedPlan) {
      steps = projectedData.projectedPlan.visibleSteps;
      planGoal = projectedData.projectedPlan.goal;
    }
    if (!steps?.length || !planGoal) return;
    const current: Record<string, string> = {};
    for (const s of steps) {
      current[s.id] = s.status;
      const prev = previousStepStatusesRef.current[s.id];
      if (prev != null && prev !== StepStatus.Completed && s.status === StepStatus.Completed) {
        showToast({
          message: `"${s.title}" in "${planGoal}" is complete.`,
          severity: 'success',
          autoHideDuration: 8000,
          action: { label: 'View step', to: `/app/plans/${id}/tasks/${s.id}` },
        });
      }
    }
    previousStepStatusesRef.current = current;
  }, [id, hasAdminAccess, planData?.planapusPlan, projectedData?.projectedPlan, showToast]);

  // Detect if plan is still being generated (preview claim flow).
  // Use plan status as the authoritative signal — GENERATING means no steps exist yet.
  // Check projectedPlan first (loads earlier) to avoid any flash of empty content.
  const planStatus = planData?.planapusPlan?.status || projectedData?.projectedPlan?.status;
  const isGenerating = planStatus === 'GENERATING';
  const pendingJobId = isGenerating ? planData?.planapusPlan?.pendingGenerationJobId : null;

  useEffect(() => {
    if (!pendingJobId) {
      setGenJobMessage(null);
      return;
    }
    const unsub = onSnapshot(doc(db, 'generation_jobs', pendingJobId), (snap) => {
      if (!snap.exists()) return;
      const data = snap.data();
      setGenJobMessage(data.inputSummary || null);
    });
    return () => unsub();
  }, [pendingJobId]);

  // Show loading while checking auth or redirecting
  if (authLoading || !user) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Loading state - wait for projected plan first
  if (projectedLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  // Error state
  if (projectedError) {
    // Check if it's an access denied error
    if (projectedError.message.includes('Access denied') ||
        projectedError.message.includes('FORBIDDEN')) {
      return (
        <Container maxWidth="md" sx={{ py: 4 }}>
          <Box
            sx={{
              textAlign: 'center',
              py: 8,
              bgcolor: 'background.paper',
              borderRadius: 2,
            }}
          >
            <LockIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h5" gutterBottom>
              Access Denied
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              You don't have permission to view this plan.
            </Typography>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/app/plans')}
              variant="contained"
            >
              Back to Plans
            </Button>
          </Box>
        </Container>
      );
    }
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">Error loading plan: {projectedError.message}</Alert>
      </Container>
    );
  }

  // No projected plan data
  if (!projectedData?.projectedPlan) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="warning">Plan not found</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/app/plans')}
          sx={{ mt: 2 }}
        >
          Back to Plans
        </Button>
      </Container>
    );
  }

  // Show generation progress — plan status is GENERATING so there's nothing to render yet.
  // Check early (before waiting for GET_PLAN) so we show the spinner immediately.
  if (isGenerating) {
    const planGoal = planData?.planapusPlan?.goal || projectedData?.projectedPlan?.goal || 'Your process';
    return (
      <Container maxWidth="sm" sx={{ py: 8 }}>
        <Box sx={{ textAlign: 'center' }}>
          <Box sx={{ position: 'relative', width: 64, height: 64, mx: 'auto', mb: 3 }}>
            <CircularProgress size={64} thickness={2} sx={{ color: colors.green[600] }} />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
            Building your process
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {planGoal}
          </Typography>
          <LinearProgress
            variant="indeterminate"
            sx={{
              height: 4,
              borderRadius: 2,
              mb: 1,
              bgcolor: colors.gray[100],
              '& .MuiLinearProgress-bar': { bgcolor: colors.green[600], borderRadius: 2 },
            }}
          />
          <Typography variant="caption" color="text.secondary">
            {genJobMessage || 'Generating detailed steps, roles, and automation actions...'}
          </Typography>
        </Box>
      </Container>
    );
  }

  // For ADMIN users, wait for full plan to load
  if (hasAdminAccess && planLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  // For ADMIN users, handle plan load error (auth errors handled by token refresh/redirect)
  if (hasAdminAccess && planError) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Alert severity="error">Error loading plan: {planError.message}</Alert>
      </Container>
    );
  }

  // Build the plan object based on access level
  // ADMIN: Use full plan from GET_PLAN
  // ROLE_MEMBER: Use projected plan (visibleSteps only)
  let plan: any;
  let steps: any[];

  if (hasAdminAccess && planData?.planapusPlan) {
    // ADMIN/Owner: Full plan access
    plan = planData.planapusPlan;
    steps = plan.steps;
  } else {
    // ROLE_MEMBER: Only see visible steps from projected plan
    const projected = projectedData.projectedPlan;
    plan = {
      id: projected.id,
      goal: projected.goal,
      summary: projected.summary,
      status: projected.status,
      createdAt: projected.createdAt,
      phases: projected.phases,
      steps: projected.visibleSteps, // Only visible steps
      // Hide sensitive data from ROLE_MEMBER
      facts: null,
      participants: [],
      roles: [],
    };
    steps = projected.visibleSteps;
  }

  // Calculate progress using the steps available to this viewer
  const planProgress = getPlanProgress(steps);

  return (
    <PlanViewerProvider
        viewerPersonId={viewerPersonId}
        accessLevel={accessLevel || 'ROLE_MEMBER'}
        roleIds={roleIds}
        isOwner={isOwner}
      >
        <Box sx={{ flex: 1, bgcolor: colors.background, minHeight: '100vh' }}>
          {/* Compact Header Bar */}
          <PlanHeaderBar
            plan={plan}
            planProgress={planProgress}
            currentTab={currentTab}
            onTabChange={handleTabChange}
          />

          {/* Access Level Indicator for ROLE_MEMBER users */}
          {!hasAdminAccess && (
            <Container maxWidth="lg" sx={{ pt: 2, pb: 0 }}>
              <Alert
                severity="info"
                icon={<LockIcon fontSize="small" />}
                sx={{
                  py: 0.5,
                  '& .MuiAlert-message': { py: 0.5 }
                }}
              >
                You're viewing steps assigned to you. Contact the plan owner for full access.
              </Alert>
            </Container>
          )}

          {/* Tab Content */}
          <Container maxWidth="lg" sx={{ py: 3 }}>
            {isPortalTab ? (
              <PortalShell
                key={`portal-${plan.id}`}
                plan={plan}
                currentTab={portalTab}
                onTabChange={handlePortalTabChange}
                onToggleStepCompletion={handleToggleStepCompletion}
              />
            ) : (
              <PlanTabContainer
                key={plan.id}
                plan={plan}
                currentTab={currentTab}
                selectedStepId={stepId || null}
                planProgress={planProgress}
                onToggleStepCompletion={handleToggleStepCompletion}
                onUpdateStepContext={handleUpdateStepContext}
                onStepSelect={handleStepSelect}
              />
            )}
          </Container>

        </Box>
      </PlanViewerProvider>
  );
}

export default PlanDetailPage;
